<template>
  <div>
    <v-toolbar color="white" flat>
      <div class="content-wrapper d-flex align-center" style="width: 100%;">
        <img :src="logoSrc" alt="Logo" class="toolbar-logo mr-2" />
        <v-toolbar-title class="text-h5 font-weight-bold"><h1 style="font-size: inherit; font-weight: inherit; margin: 0; line-height: inherit;">{{ title }}</h1></v-toolbar-title>
        <div class="flex-grow-1"></div>
        <v-btn variant="text" prepend-icon="mdi-share-variant" class="text-capitalize" @click="shareDialogOpen = true">
          Share
        </v-btn>
      </div>
    </v-toolbar>

    <v-container fluid class="pa-4">
      <!-- Airbnb-style photo grid -->
      <div class="photo-grid-wrapper">
      <div class="photo-grid" @click="openLightbox(0)">
        <!-- Main large image (left) -->
        <div v-if="images.length > 0" class="photo-main">
          <img :src="images[0]" :alt="title" class="grid-image" />
        </div>

        <!-- 4 smaller images (right, 2x2 grid) -->
        <div v-if="images.length > 1" class="photo-top-left">
          <img :src="images[1]" :alt="title" class="grid-image" @click.stop="openLightbox(1)" />
        </div>
        <div v-if="images.length > 2" class="photo-top-right">
          <img :src="images[2]" :alt="title" class="grid-image" @click.stop="openLightbox(2)" />
        </div>
        <div v-if="images.length > 3" class="photo-bottom-left">
          <img :src="images[3]" :alt="title" class="grid-image" @click.stop="openLightbox(3)" />
        </div>
        <div v-if="images.length > 4" class="photo-bottom-right">
          <img :src="images[4]" :alt="title" class="grid-image" @click.stop="openLightbox(4)" />
          <v-btn
            v-if="images.length > 5"
            class="show-all-btn"
            color="white"
            size="small"
            variant="outlined"
            @click.stop="showAllPhotos = true"
          >
            <v-icon start size="small">mdi-dots-grid</v-icon>
            Show all photos
          </v-btn>
        </div>
      </div>
      </div>

      <!-- Listing info + Check Availability sidebar -->
      <v-row class="mt-6 content-wrapper">
        <v-col cols="12" md="7">
          <h2 class="text-h5 font-weight-bold">{{ subtitle }}</h2>
          <p class="text-body-1 text-grey-darken-1 mt-1">{{ details }}</p>
          
          <!-- Host info -->
          <v-divider class="my-6"></v-divider>
          <div class="d-flex align-center cursor-pointer" @click="hostDialogOpen = true" style="transition: opacity 0.2s;" :style="{ cursor: 'pointer' }" @mouseenter="$event.target.parentElement.style.opacity = '0.7'" @mouseleave="$event.target.parentElement.style.opacity = '1'">
            <v-avatar size="48" class="mr-4">
              <img :src="hostImageSrc" :alt="hostName" style="width: 100%; height: 100%; object-fit: cover;" />
            </v-avatar>
            <div>
              <div class="font-weight-bold">Hosted by {{ hostName }}</div>
            </div>
          </div>

          <!-- Features -->
          <v-divider class="my-6"></v-divider>
          <div class="d-flex align-start mb-4">
            <v-icon class="mr-4 mt-1">mdi-cellphone-key</v-icon>
            <div>
              <div class="font-weight-bold">Self check-in</div>
              <div class="text-body-2 text-grey">Check yourself in with the keypad.</div>
            </div>
          </div>

          <!-- Description -->
          <v-divider class="my-6"></v-divider>
          <div>
            <p class="text-body-1" :class="{ 'description-truncated': !showFullDescription }">
              {{ description }}
            </p>
            <v-btn
              v-if="description.length > 200"
              variant="text"
              class="text-capitalize font-weight-bold pa-0 mt-2"
              color="black"
              @click="showFullDescription = !showFullDescription"
            >
              {{ showFullDescription ? 'Show less' : 'Show more' }}
              <v-icon end size="small">{{ showFullDescription ? 'mdi-chevron-up' : 'mdi-chevron-right' }}</v-icon>
            </v-btn>
          </div>

          <!-- What this place offers -->
          <v-divider class="my-6"></v-divider>
          <h2 class="text-h5 font-weight-bold mb-4">What this place offers</h2>
          <v-row>
            <v-col v-for="amenity in visibleAmenities" :key="amenity.label" cols="6" class="d-flex align-center py-3">
              <v-icon class="mr-4 text-grey-darken-3" size="28">{{ amenity.icon }}</v-icon>
              <span class="text-body-1">{{ amenity.label }}</span>
            </v-col>
          </v-row>
          <v-btn
            v-if="amenities.length > visibleAmenityCount"
            variant="outlined"
            color="black"
            class="text-capitalize font-weight-bold mt-4"
            rounded="lg"
            size="large"
            @click="showAllAmenities = true"
          >
            Show all {{ amenities.length }} amenities
          </v-btn>

          <!-- All amenities dialog -->
          <v-dialog v-model="showAllAmenities" max-width="560" scrollable>
            <v-card rounded="xl">
              <v-card-title class="d-flex align-center pa-4">
                <v-btn icon variant="text" size="small" @click="showAllAmenities = false">
                  <v-icon>mdi-close</v-icon>
                </v-btn>
                <span class="text-h6 font-weight-bold ml-2">What this place offers</span>
              </v-card-title>
              <v-divider></v-divider>
              <v-card-text class="pa-6">
                <div v-for="amenity in amenities" :key="amenity.label" class="d-flex align-center py-3">
                  <v-icon class="mr-4 text-grey-darken-3" size="28">{{ amenity.icon }}</v-icon>
                  <span class="text-body-1">{{ amenity.label }}</span>
                </div>
              </v-card-text>
            </v-card>
          </v-dialog>

          <!-- Availability Calendar -->
          <v-divider class="my-6"></v-divider>
          <h2 class="text-h5 font-weight-bold mb-1">Availability</h2>
          <p class="text-body-2 text-grey mb-4">{{ calendarNights }}</p>

          <div class="calendar-wrapper">
            <div v-for="month in calendarMonths" :key="month.key" class="calendar-month">
              <div class="calendar-month-header">
                <v-btn v-if="month.index === 0" icon variant="text" size="small" @click="prevMonth" :disabled="!canGoPrev">
                  <v-icon>mdi-chevron-left</v-icon>
                </v-btn>
                <div v-else style="width:28px"></div>
                <span class="font-weight-bold">{{ month.label }}</span>
                <v-btn v-if="month.index === 1" icon variant="text" size="small" @click="nextMonth">
                  <v-icon>mdi-chevron-right</v-icon>
                </v-btn>
                <div v-else style="width:28px"></div>
              </div>
              <div class="calendar-day-names">
                <span v-for="d in dayNames" :key="d" class="calendar-day-name">{{ d }}</span>
              </div>
              <div class="calendar-grid">
                <span v-for="blank in month.startDay" :key="'b' + blank" class="calendar-cell"></span>
                <span
                  v-for="day in month.days"
                  :key="day.date"
                  class="calendar-cell"
                  :class="{
                    'calendar-blocked': day.blocked && !isCheckoutCandidate(day.date),
                    'calendar-past': day.past,
                    'calendar-today': day.today,
                    'calendar-selected': day.selected,
                    'calendar-in-range': day.inRange,
                    'calendar-selectable': (!day.blocked || isCheckoutCandidate(day.date)) && !day.past
                  }"
                  @click="(!day.blocked || isCheckoutCandidate(day.date)) && !day.past && selectCalendarDate(day.date)"
                >
                  <span :class="{ 'calendar-strikethrough': day.blocked && !isCheckoutCandidate(day.date) }">{{ day.num }}</span>
                </span>
              </div>
            </div>
          </div>

          <div class="d-flex align-center mt-3">
            <div class="d-flex align-center mr-6">
              <span class="legend-box legend-blocked mr-2"></span>
              <span class="text-body-2">Blocked</span>
            </div>
            <div class="d-flex align-center mr-6">
              <span class="legend-box legend-available mr-2"></span>
              <span class="text-body-2">Available</span>
            </div>
            <div v-if="checkIn || checkOut" class="d-flex align-center">
              <span class="legend-box legend-selected mr-2"></span>
              <span class="text-body-2">Selected</span>
            </div>
          </div>

          <v-btn variant="text" class="text-capitalize font-weight-bold pa-0 mt-3" color="black" @click="clearDates">
            Clear dates
          </v-btn>

          <!-- Where you'll be -->
          <v-divider class="my-6"></v-divider>
          <h2 class="text-h5 font-weight-bold mb-4">Where you'll be</h2>
          <p class="text-body-1 mb-4">{{ locationDetails }}</p>
          
          <div class="map-container" style="border-radius: 12px; overflow: hidden; height: 400px; box-shadow: 0 2px 8px rgba(0,0,0,0.1);">
            <iframe
              width="100%"
              height="100%"
              frameborder="0"
              style="border: 0;"
              src="https://www.google.com/maps/embed/v1/place?key=AIzaSyAqbow8r-7faJzaH8u6Rkr_HfpDGQHISJE&q=33.0337,-97.0210&zoom=14"
              allowfullscreen=""
              loading="lazy"
              referrerpolicy="no-referrer-when-downgrade">
            </iframe>
          </div>
          
          <div class="mt-4 text-body-2 text-grey">
            <v-icon size="small" class="mr-2">mdi-info-outline</v-icon>
            Exact location provided after booking confirmation
          </div>
        </v-col>

        <!-- Check Availability sidebar -->
        <v-col cols="12" md="5" lg="4" class="d-md-flex justify-end" style="position: relative;">
          <v-card class="availability-card" elevation="3" rounded="xl" width="100%" max-width="380">
            <v-card-text class="pa-6" style="position: relative; overflow: visible;">
              <!-- Total price display -->
              <div v-if="totalPrice && checkIn && checkOut" class="mb-3">
                <span :class="showPriceBreakdowns ? 'total-price-highlight text-h5 font-weight-bold' : 'text-h5 font-weight-bold'">{{ grandTotal ? grandTotal.formatted : totalPrice.formatted }}</span>
                <span class="text-body-1"> total</span>
                <div class="text-body-2 text-grey-darken-1 mt-1">
                  {{ totalPrice.nights }} night{{ totalPrice.nights !== 1 ? 's' : '' }}
                </div>
              </div>
              <div v-else-if="totalPriceLoading" class="mb-3">
                <v-progress-circular indeterminate size="20" width="2" class="mr-2"></v-progress-circular>
                <span class="text-body-2 text-grey">Calculating price...</span>
              </div>
              <div v-else class="text-h6 font-weight-bold mb-3">Add dates for prices</div>

              <!-- Date inputs -->
              <div class="date-row">
                <div class="date-field date-field-left" :class="{ 'date-field-active': selecting === 'checkin' }" @click="openDatePicker('checkin')">
                  <label class="date-label">CHECK-IN</label>
                  <div class="date-input">{{ checkIn ? formatDisplay(checkIn) : 'Add date' }}</div>
                </div>
                <div class="date-field date-field-right" :class="{ 'date-field-active': selecting === 'checkout' }" @click="openDatePicker('checkout')">
                  <label class="date-label">CHECKOUT</label>
                  <div class="date-input">{{ checkOut ? formatDisplay(checkOut) : 'Add date' }}</div>
                </div>
              </div>

              <!-- Airbnb-style calendar popup -->
              <div v-if="datePickerOpen" class="datepicker-popup" v-click-outside="closeDatePicker">
                <div class="datepicker-header">
                  <h3 class="text-h6 font-weight-bold">Select dates</h3>
                  <p class="text-body-2 text-grey mt-1">Add your travel dates for exact pricing</p>
                  <p v-if="minimumBookingDays > 1" class="text-body-2 text-grey mt-1">
                    <v-icon size="x-small" class="mr-1">mdi-info</v-icon>Minimum {{ minimumBookingDays }} nights required
                  </p>
                </div>
                <div class="datepicker-months">
                  <div v-for="month in pickerCalendarMonths" :key="month.key" class="datepicker-month">
                    <div class="calendar-month-header">
                      <v-btn v-if="month.index === 0" icon variant="text" size="small" @click.stop="prevPickerMonth" :disabled="!canGoPickerPrev">
                        <v-icon>mdi-chevron-left</v-icon>
                      </v-btn>
                      <div v-else style="width:28px"></div>
                      <span class="font-weight-bold">{{ month.label }}</span>
                      <v-btn v-if="month.index === 1" icon variant="text" size="small" @click.stop="nextPickerMonth">
                        <v-icon>mdi-chevron-right</v-icon>
                      </v-btn>
                      <div v-else style="width:28px"></div>
                    </div>
                    <div class="calendar-day-names">
                      <span v-for="d in dayNames" :key="d" class="calendar-day-name">{{ d }}</span>
                    </div>
                    <div class="calendar-grid">
                      <span v-for="blank in month.startDay" :key="'pb' + blank" class="calendar-cell"></span>
                      <span
                        v-for="day in month.days"
                        :key="day.date"
                        class="calendar-cell"
                        :class="{
                          'calendar-blocked': day.blocked && !isCheckoutCandidate(day.date),
                          'calendar-past': day.past,
                          'calendar-today': day.today,
                          'calendar-selected': day.selected,
                          'calendar-in-range': day.inRange,
                          'calendar-selectable': (!day.blocked || isCheckoutCandidate(day.date)) && !day.past
                        }"
                        @click.stop="(!day.blocked || isCheckoutCandidate(day.date)) && !day.past && selectPickerDate(day.date)"
                      >
                        <span :class="{ 'calendar-strikethrough': day.blocked && !isCheckoutCandidate(day.date) }">{{ day.num }}</span>
                      </span>
                    </div>
                  </div>
                </div>
                <div class="datepicker-footer">
                  <v-icon size="small" class="mr-auto">mdi-keyboard-outline</v-icon>
                  <v-btn variant="text" class="text-capitalize text-decoration-underline" color="black" size="small" @click="clearDates">
                    Clear dates
                  </v-btn>
                  <v-btn color="black" size="small" rounded="lg" class="text-capitalize ml-2" @click="closeDatePicker">
                    Close
                  </v-btn>
                </div>
              </div>

              <!-- Guests dropdown -->
              <div class="guest-field mt-n1" @click="guestPickerOpen = !guestPickerOpen" style="cursor: pointer;">
                <label class="date-label">GUESTS</label>
                <div class="d-flex align-center justify-space-between">
                  <span class="text-body-2" style="padding: 6px 0;">{{ guestSummary }}</span>
                  <v-icon size="small">{{ guestPickerOpen ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                </div>
              </div>

              <!-- Guest picker popup -->
              <div v-if="guestPickerOpen" class="guest-picker-popup" v-click-outside="() => guestPickerOpen = false">
                <!-- Adults -->
                <div class="guest-row">
                  <div>
                    <div class="guest-row-title">Adults</div>
                    <div class="guest-row-subtitle">Age 13+</div>
                  </div>
                  <div class="d-flex align-center" style="gap: 12px;">
                    <v-btn icon variant="outlined" size="small" :disabled="adults <= 1" @click.stop="adults--">
                      <v-icon size="18">mdi-minus</v-icon>
                    </v-btn>
                    <span class="text-body-1" style="min-width: 20px; text-align: center;">{{ adults }}</span>
                    <v-btn icon variant="outlined" size="small" :disabled="totalGuestCount >= maxGuests" @click.stop="adults++">
                      <v-icon size="18">mdi-plus</v-icon>
                    </v-btn>
                  </div>
                </div>

                <!-- Children -->
                <div class="guest-row">
                  <div>
                    <div class="guest-row-title">Children</div>
                    <div class="guest-row-subtitle">Ages 2–12</div>
                  </div>
                  <div class="d-flex align-center" style="gap: 12px;">
                    <v-btn icon variant="outlined" size="small" :disabled="children <= 0" @click.stop="children--">
                      <v-icon size="18">mdi-minus</v-icon>
                    </v-btn>
                    <span class="text-body-1" style="min-width: 20px; text-align: center;">{{ children }}</span>
                    <v-btn icon variant="outlined" size="small" :disabled="totalGuestCount >= maxGuests" @click.stop="children++">
                      <v-icon size="18">mdi-plus</v-icon>
                    </v-btn>
                  </div>
                </div>

                <!-- Infants -->
                <div class="guest-row">
                  <div>
                    <div class="guest-row-title">Infants</div>
                    <div class="guest-row-subtitle">Under 2</div>
                  </div>
                  <div class="d-flex align-center" style="gap: 12px;">
                    <v-btn icon variant="outlined" size="small" :disabled="infants <= 0" @click.stop="infants--">
                      <v-icon size="18">mdi-minus</v-icon>
                    </v-btn>
                    <span class="text-body-1" style="min-width: 20px; text-align: center;">{{ infants }}</span>
                    <v-btn icon variant="outlined" size="small" :disabled="infants >= 5" @click.stop="infants++">
                      <v-icon size="18">mdi-plus</v-icon>
                    </v-btn>
                  </div>
                </div>

                <!-- Pets -->
                <div class="guest-row" style="border-bottom: none;">
                  <div>
                    <div class="guest-row-title">Pets</div>
                    <div class="guest-row-subtitle"><a href="#" class="text-decoration-underline" style="color: inherit;" @click.stop.prevent="serviceAnimalDialog = true">Bringing a service animal?</a></div>
                  </div>
                  <div class="d-flex align-center" style="gap: 12px;">
                    <v-btn icon variant="outlined" size="small" :disabled="pets <= 0" @click.stop="pets--">
                      <v-icon size="18">mdi-minus</v-icon>
                    </v-btn>
                    <span class="text-body-1" style="min-width: 20px; text-align: center;">{{ pets }}</span>
                    <v-btn icon variant="outlined" size="small" :disabled="pets >= 2" @click.stop="pets++">
                      <v-icon size="18">mdi-plus</v-icon>
                    </v-btn>
                  </div>
                </div>

                <div class="text-body-2 text-grey-darken-1 mt-2 mb-3">
                  This place has a maximum of {{ maxGuests }} guests, not including infants. If you're bringing more than 2 pets, please let your host know.
                </div>

                <div class="d-flex justify-end">
                  <v-btn variant="text" class="text-capitalize text-decoration-underline font-weight-bold" color="black" @click.stop="guestPickerOpen = false">
                    Close
                  </v-btn>
                </div>
              </div>

              <!-- Price breakdown -->
              <div v-if="totalPrice && checkIn && checkOut" class="price-breakdown mt-4">
                <template v-if="showPriceBreakdowns">
                  <div class="d-flex justify-space-between text-body-2 mb-2">
                    <span class="text-decoration-underline">{{ totalPrice.formatted }} x {{ totalPrice.nights }} night{{ totalPrice.nights !== 1 ? 's' : '' }}</span>
                    <span>{{ totalPrice.formatted }}</span>
                  </div>
                  <div v-for="item in feeLineItems" :key="item.name" class="d-flex justify-space-between text-body-2 mb-2">
                    <span class="text-decoration-underline">{{ item.name }}</span>
                    <span>{{ item.formatted }}</span>
                  </div>
                  <div v-for="disc in applicableDiscounts" :key="disc.name" class="d-flex justify-space-between text-body-2 mb-2" style="color: #008a05;">
                    <span>{{ disc.name }}</span>
                    <span>{{ disc.formatted }}</span>
                  </div>
                  <v-divider class="my-3"></v-divider>
                  <div class="d-flex justify-space-between font-weight-bold">
                    <span>Total</span>
                    <span>{{ grandTotal ? grandTotal.formatted : totalPrice.formatted }}</span>
                  </div>
                </template>
                <template v-else>
                  <div class="d-flex justify-space-between text-body-2 mb-2">
                    <span>{{ pricePerNight }} x {{ totalPrice.nights }} night{{ totalPrice.nights !== 1 ? 's' : '' }}</span>
                    <span>{{ totalBeforeTaxAndDiscount ? totalBeforeTaxAndDiscount.formatted : totalPrice.formatted }}</span>
                  </div>
                  <div v-for="disc in applicableDiscounts" :key="disc.name" class="d-flex justify-space-between text-body-2 mb-2" style="color: #008a05;">
                    <span>{{ disc.name }}</span>
                    <span>{{ disc.formatted }}</span>
                  </div>
                </template>
              </div>

              <!-- Minimum booking days error -->
              <v-alert v-if="minimumBookingError" type="warning" variant="tonal" class="mt-4 mb-2">
                <div class="text-body-2">
                  Minimum {{ minimumBookingDays }} night{{ minimumBookingDays !== 1 ? 's' : '' }} required. Please select a checkout date at least {{ minimumBookingDays }} day{{ minimumBookingDays !== 1 ? 's' : '' }} after check-in.
                </div>
              </v-alert>

              <!-- Reserve / Check availability button -->
              <v-btn
                block
                size="x-large"
                color="#E31C5F"
                class="mt-4 text-white text-capitalize font-weight-bold"
                rounded="pill"
                :disabled="minimumBookingError || !hasValidNights"
                @click="checkAvailability"
              >
                {{ checkIn && checkOut && totalPrice ? 'Reserve' : 'Check availability' }}
              </v-btn>
              <div v-if="checkIn && checkOut && totalPrice" class="text-center text-body-2 text-grey-darken-1 mt-2">
                You won't be charged yet
              </div>
            </v-card-text>

            <!-- Report listing -->
            <div class="text-center pb-4">
              <v-btn variant="text" class="text-capitalize text-grey-darken-1" prepend-icon="mdi-flag-outline" size="small">
                Report this listing
              </v-btn>
            </div>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <!-- Lightbox dialog -->
    <v-dialog v-model="lightboxOpen" max-width="900" content-class="lightbox-dialog">
      <v-card color="black" flat>
        <v-btn
          icon
          variant="text"
          color="white"
          class="lightbox-close"
          @click="lightboxOpen = false"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
        <div class="d-flex align-center justify-center" style="min-height: 500px; position: relative;">
          <v-btn
            icon
            variant="text"
            color="white"
            class="lightbox-nav lightbox-prev"
            @click="prevImage"
            :disabled="lightboxIndex === 0"
          >
            <v-icon size="large">mdi-chevron-left</v-icon>
          </v-btn>

          <img
            :src="images[lightboxIndex]"
            :alt="title"
            class="lightbox-image"
          />

          <v-btn
            icon
            variant="text"
            color="white"
            class="lightbox-nav lightbox-next"
            @click="nextImage"
            :disabled="lightboxIndex === images.length - 1"
          >
            <v-icon size="large">mdi-chevron-right</v-icon>
          </v-btn>
        </div>
        <div class="text-center text-white text-body-2 pb-3">
          {{ lightboxIndex + 1 }} / {{ images.length }}
        </div>
      </v-card>
    </v-dialog>

    <!-- Show All Photos dialog -->
    <v-dialog v-model="showAllPhotos" fullscreen transition="dialog-bottom-transition">
      <v-card>
        <v-toolbar color="white" flat>
          <v-btn icon @click="showAllPhotos = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
          <v-toolbar-title v-if="sortMode" class="text-body-1 font-weight-bold">Drag images to reorder</v-toolbar-title>
          <div class="flex-grow-1"></div>
          <v-btn
            variant="text"
            :prepend-icon="sortMode ? 'mdi-check' : 'mdi-sort'"
            class="text-capitalize"
            :color="sortMode ? 'green' : 'black'"
            @click="toggleSortMode"
          >
            {{ sortMode ? 'Done sorting' : 'Sort images' }}
          </v-btn>
        </v-toolbar>
        <v-container style="max-width: 800px;">
          <template v-if="sortMode">
            <div
              v-for="(item, i) in sortedAssetItems"
              :key="item.filename"
              class="mb-4 sort-item"
              draggable="true"
              @dragstart="onDragStart(i)"
              @dragover="onDragOver($event)"
              @drop="onDrop(i)"
              @dragend="onDragEnd"
              :class="{ 'sort-item-dragging': dragIndex === i }"
            >
              <div class="sort-handle d-flex align-center">
                <v-icon class="mr-2" size="small">mdi-drag</v-icon>
                <span class="text-body-2 text-grey-darken-1">{{ i + 1 }}</span>
              </div>
              <v-img
                :src="item.src"
                :aspect-ratio="16/10"
                cover
                rounded="lg"
              />
            </div>
          </template>
          <template v-else>
            <div v-for="(img, i) in images" :key="i" class="mb-4">
              <v-img
                :src="img"
                :aspect-ratio="16/10"
                cover
                rounded="lg"
                class="cursor-pointer"
                @click="openLightbox(i); showAllPhotos = false"
              />
            </div>
          </template>
        </v-container>
      </v-card>
    </v-dialog>

    <!-- Service Animals dialog -->
    <v-dialog v-model="serviceAnimalDialog" max-width="500">
      <v-card rounded="xl">
        <v-card-title class="d-flex align-center pa-4 pb-0">
          <v-btn icon variant="text" size="small" @click="serviceAnimalDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pa-6 pt-2">
          <h2 class="text-h5 font-weight-bold mb-3">Service animals</h2>
          <p class="text-body-1 mb-4" style="color: #222;">
            Service animals aren't pets, so there's no need to add them here.
          </p>

        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Host Info dialog -->
    <v-dialog v-model="hostDialogOpen" max-width="500">
      <v-card rounded="xl">
        <v-card-title class="d-flex align-center pa-4 pb-0">
          <v-btn icon variant="text" size="small" @click="hostDialogOpen = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pa-6">
          <div class="text-center mb-6">
            <v-avatar size="120" class="mb-4">
              <img :src="hostImageSrc" :alt="hostName" style="width: 100%; height: 100%; object-fit: cover;" />
            </v-avatar>
            <h2 class="text-h4 font-weight-bold">{{ hostName }}</h2>
            <p class="text-body-2 text-grey">Host</p>
          </div>

          <!-- Host Stats -->
          <div class="d-flex justify-center gap-6 mb-6">
            <div class="text-center">
              <div class="text-h6 font-weight-bold">{{ hostReviews }}</div>
              <div class="text-body-2 text-grey">Reviews</div>
            </div>
            <div class="text-center">
              <div class="text-h6 font-weight-bold">{{ hostRating }}.0</div>
              <div class="text-body-2 text-grey">Rating</div>
            </div>
            <div class="text-center">
              <div class="text-h6 font-weight-bold">{{ hostExperience }}</div>
              <div class="text-body-2 text-grey">Month hosting</div>
            </div>
          </div>

          <v-divider class="mb-4"></v-divider>

          <!-- About Host -->
          <h3 class="text-h6 font-weight-bold mb-4">About {{ hostName }}</h3>
          <div class="mb-4 text-body-2">
            <div class="d-flex align-center mb-2">
              <v-icon class="mr-2" size="small">mdi-briefcase</v-icon>
              <span>My work: {{ hostInfo.work }}</span>
            </div>
            <div class="d-flex align-center mb-2">
              <v-icon class="mr-2" size="small">mdi-map-marker</v-icon>
              <span>{{ hostInfo.location }}</span>
            </div>
            <div class="d-flex align-center mb-2">
              <v-icon class="mr-2" size="small">mdi-school</v-icon>
              <span>{{ hostInfo.school }}</span>
            </div>
          </div>

          <v-divider class="mb-4"></v-divider>

          <!-- Host Facts -->
          <div class="text-body-2 text-grey">
            <div class="mb-2">{{ hostInfo.fact1 }}</div>
            <div class="mb-2">{{ hostInfo.fact2 }}</div>
            <div class="mb-2">{{ hostInfo.fact3 }}</div>
          </div>

          <v-divider class="my-4"></v-divider>

          <!-- About Home -->
          <p class="text-body-2">{{ hostInfo.description }}</p>

          <v-btn block size="large" color="black" class="text-white text-capitalize font-weight-bold rounded-pill mt-4" @click="hostDialogOpen = false">
            Close
          </v-btn>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- PayPal Checkout dialog -->
    <v-dialog v-model="paypalCheckoutOpen" max-width="600" persistent>
      <v-card rounded="xl">
        <v-card-title class="d-flex align-center pa-5 pb-0">
          <span class="text-h6 font-weight-bold">Complete your booking</span>
          <v-spacer></v-spacer>
          <v-btn icon variant="text" @click="closePayPalCheckout">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pa-5">
          <!-- Booking summary -->
          <div v-if="totalPrice" class="mb-4 pa-4 bg-grey-lighten-4" style="border-radius: 12px;">
            <div class="text-subtitle-1 font-weight-bold mb-2">Booking Summary</div>
            <div class="d-flex justify-space-between text-body-2 mb-1">
              <span>Check-in</span>
              <span class="font-weight-medium">{{ formatDisplay(checkIn) }}</span>
            </div>
            <div class="d-flex justify-space-between text-body-2 mb-1">
              <span>Checkout</span>
              <span class="font-weight-medium">{{ formatDisplay(checkOut) }}</span>
            </div>
            <div class="d-flex justify-space-between text-body-2 mb-1">
              <span>Guests</span>
              <span class="font-weight-medium">{{ guestCount }}</span>
            </div>
            <v-divider class="my-2"></v-divider>
            <template v-if="showPriceBreakdowns">
              <div class="d-flex justify-space-between text-body-2 mb-1">
                <span>{{ totalPrice.formatted }} x {{ totalPrice.nights }} night{{ totalPrice.nights !== 1 ? 's' : '' }}</span>
                <span>{{ totalPrice.formatted }}</span>
              </div>
              <div v-for="item in feeLineItems" :key="'pp-' + item.name" class="d-flex justify-space-between text-body-2 mb-1">
                <span>{{ item.name }}</span>
                <span>{{ item.formatted }}</span>
              </div>
              <div v-for="item in applicableDiscounts" :key="'pp-disc-' + item.name" class="d-flex justify-space-between text-body-2 mb-1" style="color: #008a05;">
                <span>{{ item.name }}</span>
                <span>-{{ item.formatted }}</span>
              </div>
              <v-divider class="my-2"></v-divider>
              <div class="d-flex justify-space-between font-weight-bold">
                <span>Total</span>
                <span>{{ grandTotal ? grandTotal.formatted : totalPrice.formatted }}</span>
              </div>
            </template>
            <template v-else>
              <div class="d-flex justify-space-between text-body-2 mb-1">
                <span>{{ pricePerNight }} x {{ totalPrice.nights }} night{{ totalPrice.nights !== 1 ? 's' : '' }}</span>
                <span>{{ totalBeforeTaxAndDiscount ? totalBeforeTaxAndDiscount.formatted : totalPrice.formatted }}</span>
              </div>
              <div v-for="item in applicableDiscounts" :key="'pp-disc2-' + item.name" class="d-flex justify-space-between text-body-2 mb-1" style="color: #008a05;">
                <span>{{ item.name }}</span>
                <span>-{{ item.formatted }}</span>
              </div>
              <div class="d-flex justify-space-between text-body-2 mb-1">
                <span>Taxes</span>
                <span>{{ taxTotal }}</span>
              </div>
              <v-divider class="my-2"></v-divider>
              <div class="d-flex justify-space-between font-weight-bold">
                <span>Total</span>
                <span>{{ grandTotal ? grandTotal.formatted : totalPrice.formatted }}</span>
              </div>
            </template>
          </div>

          <!-- Payment result -->
          <v-alert v-if="paymentSuccess" type="success" variant="tonal" class="mb-4">
            <div class="font-weight-bold">Payment successful!</div>
            <div class="text-body-2">{{ paymentMessage }}</div>
          </v-alert>
          <v-alert v-if="paymentError" :type="paymentSuccess ? 'warning' : 'error'" variant="tonal" class="mb-4">
            <div class="font-weight-bold">{{ paymentSuccess ? 'Warning' : 'Payment failed' }}</div>
            <div class="text-body-2">{{ paymentError }}</div>
          </v-alert>

          <!-- PayPal buttons container -->
          <div v-if="!paymentSuccess">
            <v-text-field
              v-model="guestName"
              label="Full name"
              variant="outlined"
              density="comfortable"
              :rules="[v => !!v && v.trim().length > 0 || 'Full name is required']"
              class="mb-4"
            ></v-text-field>
            <div v-if="paypalLoading" class="text-center py-6">
              <v-progress-circular indeterminate color="#003087" size="40"></v-progress-circular>
              <div class="text-body-2 text-grey mt-2">Loading payment options...</div>
            </div>
            <div id="paypal-button-container" class="mb-4"></div>

            <!-- Card fields -->
            <div v-if="cardFieldsEligible" id="card-form" class="card-form-container">
              <v-divider class="mb-4"></v-divider>
              <div class="text-subtitle-2 font-weight-bold mb-3">Or pay with card</div>
              <v-text-field
                v-model="cardEmail"
                label="Email"
                type="email"
                variant="outlined"
                density="comfortable"
                hide-details="auto"
                class="mb-3"
              ></v-text-field>
              <div id="card-name-field-container" class="mb-3"></div>
              <div id="card-number-field-container" class="mb-3"></div>
              <div class="d-flex" style="gap: 12px;">
                <div id="card-expiry-field-container" style="flex: 1;"></div>
                <div id="card-cvv-field-container" style="flex: 1;"></div>
              </div>
              <v-btn
                block
                size="large"
                color="#003087"
                class="mt-4 text-white text-capitalize font-weight-bold"
                rounded="lg"
                :loading="cardPaymentProcessing"
                @click="submitCardPayment"
              >
                <v-icon start>mdi-credit-card-outline</v-icon>
                Pay with Card
              </v-btn>
            </div>
          </div>

          <!-- Done button after success -->
          <v-btn
            v-if="paymentSuccess"
            block
            size="large"
            color="#E31C5F"
            class="text-white text-capitalize font-weight-bold"
            rounded="pill"
            @click="closePayPalCheckout"
          >
            Done
          </v-btn>
        </v-card-text>
      </v-card>
    </v-dialog>
    <!-- Share dialog -->
    <v-dialog v-model="shareDialogOpen" max-width="560">
      <v-card rounded="xl">
        <v-card-title class="d-flex align-center pa-4">
          <v-btn icon variant="text" size="small" @click="shareDialogOpen = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
          <span class="text-h6 font-weight-bold ml-2">Share this place</span>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pa-6">
          <!-- Property preview -->
          <div class="d-flex align-center mb-6">
            <img v-if="images.length > 0" :src="images[0]" :alt="title" class="share-preview-image mr-4" />
            <div>
              <div class="text-body-1 font-weight-medium">{{ subtitle }}</div>
              <div class="text-body-2 text-grey-darken-1">{{ isNew ? 'New' : ('\u2605' + hostRating + '.0') }} · {{ details }}</div>
            </div>
          </div>

          <!-- Share options grid -->
          <div class="share-options-grid">
            <button class="share-option-btn" @click="copyShareLink">
              <v-icon size="24" class="mb-1">mdi-content-copy</v-icon>
              <span class="text-body-2">Copy Link</span>
            </button>
            <button class="share-option-btn" @click="shareViaEmail">
              <v-icon size="24" class="mb-1">mdi-email-outline</v-icon>
              <span class="text-body-2">Email</span>
            </button>
            <button class="share-option-btn" @click="shareViaMessages">
              <v-icon size="24" class="mb-1">mdi-message-outline</v-icon>
              <span class="text-body-2">Messages</span>
            </button>
            <button class="share-option-btn" @click="shareViaWhatsApp">
              <v-icon size="24" class="mb-1">mdi-whatsapp</v-icon>
              <span class="text-body-2">WhatsApp</span>
            </button>
            <button class="share-option-btn" @click="shareViaMessenger">
              <v-icon size="24" class="mb-1">mdi-facebook-messenger</v-icon>
              <span class="text-body-2">Messenger</span>
            </button>
            <button class="share-option-btn" @click="shareViaFacebook">
              <v-icon size="24" class="mb-1">mdi-facebook</v-icon>
              <span class="text-body-2">Facebook</span>
            </button>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Copy link snackbar -->
    <v-snackbar v-model="linkCopiedSnackbar" :timeout="2000" color="black">
      Link copied to clipboard!
    </v-snackbar>

    <!-- Contact Us -->
    <v-footer class="bg-grey-darken-4 text-white py-8">
      <v-container>
        <v-row justify="center">
          <v-col cols="12" class="text-center">
            <h3 class="text-h6 font-weight-bold mb-2">Contact Us</h3>
            <p class="text-body-2 mb-3">Have questions or need help? Reach out to us anytime.</p>
            <a href="mailto:admin@bookstaybnb.com" class="text-white text-body-1 font-weight-medium" style="text-decoration: none;">
              <v-icon class="mr-1" size="small">mdi-email-outline</v-icon>
              admin@bookstaybnb.com
            </a>
          </v-col>
        </v-row>
      </v-container>
    </v-footer>
  </div>
</template>

<script>
import api from './backend-api'
import dbbLogo from './dbb-logo.png'
import hostImage from './assets/host.png'

// Dynamically import all images from ./hillwood using Webpack's require.context
function loadAssetImages() {
  const context = require.context('./hillwood', false, /\.(jpe?g|png|gif|webp|svg)$/i)
  return context.keys().map(key => ({
    filename: key.replace('./', ''),
    src: context(key)
  }))
}

// Generate all dates in a range (inclusive)
function dateRange(startStr, endStr) {
  const dates = []
  const start = new Date(startStr + 'T00:00:00')
  const end = new Date(endStr + 'T00:00:00')
  for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
    dates.push(formatDate(new Date(d)))
  }
  return dates
}

function formatDate(d) {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

export default {
  name: 'Hillwood',
  props: {
    title: {
      type: String,
      default: 'Stylish Newly Remodeled Home with Putting Green'
    },
    subtitle: {
      type: String,
      default: 'Entire home in Lewisville, Texas'
    },
    details: {
      type: String,
      default: '8 guests · 3 bedrooms · 5 beds · 2 baths'
    },
    photos: {
      type: Array,
      default: null
    },
    isNew: {
      type: Boolean,
      default: true
    },
    reviewCount: {
      type: Number,
      default: 1
    },
    hostName: {
      type: String,
      default: 'Dewi'
    },
    description: {
      type: String,
      default: 'Peaceful, fully furnished home ideal for families and relocating professionals, with cozy living areas and everything needed for day-to-day. Set in a quiet, safe neighborhood near downtown Lewisville dining, shopping, and entertainment. Designed for short- or mid-term stays with flexible sleeping, cozy living spaces, and everyday essentials. Relax by the fire pit or private putting green, with easy highway access to hospitals, DFW Airport, Lake Lewisville, and nearby cities.'
    },
    maxGuests: {
      type: Number,
      default: 8
    }
  },
  data() {
    return {
      logoSrc: dbbLogo,
      hostImageSrc: hostImage,
      lightboxOpen: false,
      lightboxIndex: 0,
      showAllPhotos: false,
      showFullDescription: false,
      checkIn: '',
      checkOut: '',
      adults: 1,
      children: 0,
      infants: 0,
      pets: 0,
      guestPickerOpen: false,
      serviceAnimalDialog: false,
      datePickerOpen: false,
      selecting: null,
      pickerStart: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
      calendarStart: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
      dayNames: ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'],
      blockedDates: [],
      blockedDatesLoading: false,
      assetImages: loadAssetImages(),
      imageSortOrder: [],
      sortMode: false,
      dragIndex: null,
      totalPrice: null,
      totalPriceLoading: false,
      fees: [],
      discounts: [],
      propertyId: null,
      showAllAmenities: false,
      visibleAmenityCount: 10,
      // PayPal checkout
      paypalCheckoutOpen: false,
      paypalLoading: false,
      paypalClientId: null,
      paypalSdkLoaded: false,
      cardFieldsEligible: false,
      cardFieldInstance: null,
      cardPaymentProcessing: false,
      cardEmail: '',
      guestName: '',
      paymentSuccess: false,
      paymentError: null,
      paymentMessage: '',
      // Host dialog
      showPriceBreakdowns: true,
      hostDialogOpen: false,
      shareDialogOpen: false,
      linkCopiedSnackbar: false,
      hostReviews: 5,
      hostRating: 5,
      hostExperience: 1,
      hostInfo: {
        work: 'I\'m an analyst',
        location: 'Lives in Coppell, TX',
        school: 'Where I went to school: UT Dallas',
        fact1: 'Where I\'ve always wanted to go: Pilgrimage sites',
        fact2: 'Favorite song in high school: Backstreet Boys',
        fact3: 'For guests, I always: Starter kitchen and coffee',
        description: 'I\'m Dewi, I love travelling, reading, decorating home and spending time with my family. I hope you have a good time in our house.'
      },
      // Location
      locationDetails: 'Lewisville, Texas, United States',
      propertyAddress: '1002 Hillwood Dr, Lewisville, TX 75067',
      minimumBookingDays: 2,
      minimumBookingError: false,
      amenities: [
        { icon: 'mdi-countertop-outline', label: 'Kitchen' },
        { icon: 'mdi-wifi', label: 'Wifi' },
        { icon: 'mdi-desk', label: 'Dedicated workspace' },
        { icon: 'mdi-car-outline', label: 'Free parking on premises' },
        { icon: 'mdi-paw', label: 'Pets allowed' },
        { icon: 'mdi-television', label: 'TV' },
        { icon: 'mdi-washing-machine', label: 'Washer' },
        { icon: 'mdi-tumble-dryer', label: 'Dryer' },
        { icon: 'mdi-snowflake', label: 'Air conditioning' },
        { icon: 'mdi-fire', label: 'Fire pit' },
        { icon: 'mdi-grill-outline', label: 'BBQ grill' },
        { icon: 'mdi-hanger', label: 'Hangers' },
        { icon: 'mdi-iron-outline', label: 'Iron' },
        { icon: 'mdi-hair-dryer-outline', label: 'Hair dryer' },
        { icon: 'mdi-bed-outline', label: 'Bed linens' },
        { icon: 'mdi-coffee-maker', label: 'Coffee maker' },
        { icon: 'mdi-fridge-outline', label: 'Refrigerator' },
        { icon: 'mdi-microwave', label: 'Microwave' },
        { icon: 'mdi-stove', label: 'Stove' },
        { icon: 'mdi-silverware-fork-knife', label: 'Dishes and silverware' },
        { icon: 'mdi-toaster-oven', label: 'Oven' },
        { icon: 'mdi-smoke-detector-variant', label: 'Smoke alarm' },
        { icon: 'mdi-fire-extinguisher', label: 'Fire extinguisher' },
        { icon: 'mdi-medical-bag', label: 'First aid kit' },
        { icon: 'mdi-shield-check-outline', label: 'Carbon monoxide alarm' },
        { icon: 'mdi-lock', label: 'Lock on bedroom door' },
        { icon: 'mdi-radiator', label: 'Heating' },
        { icon: 'mdi-water-boiler', label: 'Hot water' },
        { icon: 'mdi-book-open-variant', label: 'Books and reading material' },
        { icon: 'mdi-gamepad-variant-outline', label: 'Game console - PS3' },
        { icon: 'mdi-grill', label: 'Outdoor dining area' },
        { icon: 'mdi-baby-carriage', label: 'High chair' },
        { icon: 'mdi-balcony', label: 'Patio or balcony' },
        { icon: 'mdi-grass', label: 'Backyard' },
        { icon: 'mdi-key-variant', label: 'Self check-in' },
        { icon: 'mdi-clock-check-outline', label: 'Long term stays allowed' },
        { icon: 'mdi-broom', label: 'Cleaning products' },
        { icon: 'mdi-cup-water', label: 'Cooking basics' },
        { icon: 'mdi-toaster', label: 'Toaster' },
        { icon: 'mdi-blender', label: 'Blender' },
        { icon: 'mdi-trash-can-outline', label: 'Trash compactor' },
        { icon: 'mdi-food-variant', label: 'Dining table' },
        { icon: 'mdi-bathtub-outline', label: 'Bathtub' },
        { icon: 'mdi-shaker-outline', label: 'Cleaning available during stay' },
        { icon: 'mdi-bag-suitcase-outline', label: 'Luggage dropoff allowed' },
        { icon: 'mdi-cctv', label: 'Security cameras on front exterior' }
      ]
    }
  },
  mounted() {
    this.resolveProperty().then(() => {
      this.fetchBlockedDates()
      this.fetchImageSortOrder()
      this.fetchFees()
      this.fetchDiscounts()
    })
    this.fetchMinimumBookingDays()
    this.fetchShowPriceBreakdowns()
    this.recordPageVisit()
  },
  watch: {
    checkOut(val) {
      if (val && this.checkIn) {
        this.fetchTotalPrice()
      } else {
        this.totalPrice = null
      }
    }
  },
  computed: {
    images() {
      if (this.photos && this.photos.length > 0) return this.photos
      const assets = this.assetImages
      if (this.imageSortOrder.length > 0) {
        const orderMap = {}
        this.imageSortOrder.forEach((filename, index) => {
          orderMap[filename] = index
        })
        const sorted = [...assets].sort((a, b) => {
          const posA = orderMap[a.filename] !== undefined ? orderMap[a.filename] : 9999
          const posB = orderMap[b.filename] !== undefined ? orderMap[b.filename] : 9999
          return posA - posB
        })
        return sorted.map(img => img.src)
      }
      return assets.map(img => img.src)
    },
    sortedAssetItems() {
      const assets = this.assetImages
      if (this.imageSortOrder.length > 0) {
        const orderMap = {}
        this.imageSortOrder.forEach((filename, index) => {
          orderMap[filename] = index
        })
        return [...assets].sort((a, b) => {
          const posA = orderMap[a.filename] !== undefined ? orderMap[a.filename] : 9999
          const posB = orderMap[b.filename] !== undefined ? orderMap[b.filename] : 9999
          return posA - posB
        })
      }
      return assets
    },
    guestSummary() {
      const parts = []
      const guestTotal = this.adults + this.children
      parts.push(guestTotal === 1 ? '1 guest' : `${guestTotal} guests`)
      if (this.infants > 0) parts.push(this.infants === 1 ? '1 infant' : `${this.infants} infants`)
      if (this.pets > 0) parts.push(this.pets === 1 ? '1 pet' : `${this.pets} pets`)
      return parts.join(', ')
    },
    totalGuestCount() {
      return this.adults + this.children
    },
    canGoPrev() {
      const now = new Date()
      return this.calendarStart > new Date(now.getFullYear(), now.getMonth(), 1)
    },
    calendarMonths() {
      const months = []
      for (let m = 0; m < 2; m++) {
        const date = new Date(this.calendarStart.getFullYear(), this.calendarStart.getMonth() + m, 1)
        const year = date.getFullYear()
        const month = date.getMonth()
        const daysInMonth = new Date(year, month + 1, 0).getDate()
        const startDay = date.getDay()
        const today = formatDate(new Date())
        const days = []
        for (let d = 1; d <= daysInMonth; d++) {
          const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`
          days.push({
            num: d,
            date: dateStr,
            blocked: this.blockedDates.includes(dateStr),
            past: dateStr < today,
            today: dateStr === today,
            selected: dateStr === this.checkIn || dateStr === this.checkOut,
            inRange: this.checkIn && this.checkOut && dateStr > this.checkIn && dateStr < this.checkOut
          })
        }
        const label = date.toLocaleString('default', { month: 'long', year: 'numeric' })
        months.push({ key: `${year}-${month}`, label, days, startDay, index: m })
      }
      return months
    },
    calendarNights() {
      if (this.checkIn && this.checkOut) {
        const a = new Date(this.checkIn + 'T00:00:00')
        const b = new Date(this.checkOut + 'T00:00:00')
        const nights = Math.round((b - a) / (1000 * 60 * 60 * 24))
        return `${nights} night${nights !== 1 ? 's' : ''} selected`
      }
      return 'Select check-in date'
    },
    visibleAmenities() {
      return this.amenities.slice(0, this.visibleAmenityCount)
    },
    feeLineItems() {
      if (!this.totalPrice || this.fees.length === 0) return []
      const items = this.fees
        .filter(fee => fee.name.toLowerCase() !== 'pet fee')
        .map(fee => {
          let value = 0
          if (fee.amount != null) {
            value = fee.amount
          } else if (fee.percentage != null) {
            value = Math.round(this.totalPrice.totalAmount * fee.percentage / 100)
          }
          return {
            name: fee.name,
            amount: value,
            formatted: '$' + (value / 100).toFixed(2)
          }
        })
      if (this.pets >= 1) {
        const petFee = 7500 // $75.00 flat fee
        items.push({
          name: 'Pet fee',
          amount: petFee,
          formatted: '$' + (petFee / 100).toFixed(2)
        })
      }
      return items
    },
    feesTotal() {
      return this.feeLineItems.reduce((sum, item) => sum + item.amount, 0)
    },
    applicableDiscounts() {
      if (!this.totalPrice || !this.checkIn || !this.checkOut) return []
      const nights = this.totalPrice.nights
      const today = new Date()
      const checkInDate = new Date(this.checkIn + 'T00:00:00')
      const daysUntilArrival = Math.round((checkInDate - today) / (1000 * 60 * 60 * 24))
      const candidates = []

      // Best LENGTH_OF_STAY: largest threshold the booking qualifies for
      const losMatches = this.discounts
        .filter(d => d.type === 'LENGTH_OF_STAY' && nights >= d.thresholdDays)
        .sort((a, b) => b.thresholdDays - a.thresholdDays)
      if (losMatches.length > 0) {
        const d = losMatches[0]
        const amount = Math.round(this.totalPrice.totalAmount * d.percentage / 100)
        candidates.push({
          name: `discount`,
          type: 'LENGTH_OF_STAY',
          percentage: d.percentage,
          amount: amount,
          formatted: '-$' + (amount / 100).toFixed(2)
        })
      }

      // Best LAST_MINUTE: smallest threshold that still covers the days
      const lmMatches = this.discounts
        .filter(d => d.type === 'LAST_MINUTE' && daysUntilArrival >= 0 && daysUntilArrival <= d.thresholdDays)
        .sort((a, b) => a.thresholdDays - b.thresholdDays)
      if (lmMatches.length > 0) {
        const d = lmMatches[0]
        const amount = Math.round(this.totalPrice.totalAmount * d.percentage / 100)
        candidates.push({
          name: `discount`,
          type: 'LAST_MINUTE',
          percentage: d.percentage,
          amount: amount,
          formatted: '-$' + (amount / 100).toFixed(2)
        })
      }

      // Only apply the single highest discount across both types
      if (candidates.length === 0) return []
      candidates.sort((a, b) => b.amount - a.amount)
      return [candidates[0]]
    },
    discountTotal() {
      return this.applicableDiscounts.reduce((sum, item) => sum + item.amount, 0)
    },
    grandTotal() {
      if (!this.totalPrice) return null
      const total = this.totalPrice.totalAmount + this.feesTotal - this.discountTotal
      return {
        amount: total,
        formatted: '$' + (total / 100).toFixed(2)
      }
    },
    totalWithoutTax() {
      if (!this.totalPrice) return null
      const taxTotal = this.feeLineItems
        .filter(item => item.name.toLowerCase().includes('tax'))
        .reduce((sum, item) => sum + item.amount, 0)
      const total = this.totalPrice.totalAmount + this.feesTotal - taxTotal - this.discountTotal
      return {
        amount: total,
        formatted: '$' + (total / 100).toFixed(2)
      }
    },
    taxTotal() {
      const amount = this.feeLineItems
        .filter(item => item.name.toLowerCase().includes('tax'))
        .reduce((sum, item) => sum + item.amount, 0)
      return '$' + (amount / 100).toFixed(2)
    },
    totalBeforeTaxAndDiscount() {
      if (!this.totalPrice) return null
      const taxTotal = this.feeLineItems
        .filter(item => item.name.toLowerCase().includes('tax'))
        .reduce((sum, item) => sum + item.amount, 0)
      const total = this.totalPrice.totalAmount + this.feesTotal - taxTotal
      return {
        amount: total,
        formatted: '$' + (total / 100).toFixed(2)
      }
    },
    pricePerNight() {
      if (!this.totalPrice || !this.totalPrice.nights) return '$0.00'
      const total = this.totalBeforeTaxAndDiscount ? this.totalBeforeTaxAndDiscount.amount : this.totalPrice.totalAmount
      const perNight = total / this.totalPrice.nights
      return '$' + (perNight / 100).toFixed(2)
    },
    canGoPickerPrev() {
      const now = new Date()
      return this.pickerStart > new Date(now.getFullYear(), now.getMonth(), 1)
    },
    hasValidNights() {
      if (!this.checkIn || !this.checkOut) return true
      const a = new Date(this.checkIn + 'T00:00:00')
      const b = new Date(this.checkOut + 'T00:00:00')
      const nights = Math.round((b - a) / (1000 * 60 * 60 * 24))
      return nights >= this.minimumBookingDays
    },
    pickerCalendarMonths() {
      const months = []
      for (let m = 0; m < 2; m++) {
        const date = new Date(this.pickerStart.getFullYear(), this.pickerStart.getMonth() + m, 1)
        const year = date.getFullYear()
        const month = date.getMonth()
        const daysInMonth = new Date(year, month + 1, 0).getDate()
        const startDay = date.getDay()
        const today = formatDate(new Date())
        const days = []
        for (let d = 1; d <= daysInMonth; d++) {
          const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`
          days.push({
            num: d,
            date: dateStr,
            blocked: this.blockedDates.includes(dateStr),
            past: dateStr < today,
            today: dateStr === today,
            selected: dateStr === this.checkIn || dateStr === this.checkOut,
            inRange: this.checkIn && this.checkOut && dateStr > this.checkIn && dateStr < this.checkOut
          })
        }
        const label = date.toLocaleString('default', { month: 'long', year: 'numeric' })
        months.push({ key: `p${year}-${month}`, label, days, startDay, index: m })
      }
      return months
    }
  },
  methods: {
    getShareUrl() {
      return window.location.href
    },
    getShareText() {
      return `Check out this place: ${this.title} - ${this.subtitle}`
    },
    async copyShareLink() {
      try {
        await navigator.clipboard.writeText(this.getShareUrl())
        this.linkCopiedSnackbar = true
        this.shareDialogOpen = false
      } catch {
        // Fallback for older browsers
        const textarea = document.createElement('textarea')
        textarea.value = this.getShareUrl()
        textarea.style.position = 'fixed'
        textarea.style.opacity = '0'
        document.body.appendChild(textarea)
        textarea.select()
        document.execCommand('copy')
        document.body.removeChild(textarea)
        this.linkCopiedSnackbar = true
        this.shareDialogOpen = false
      }
    },
    shareViaEmail() {
      const subject = encodeURIComponent(this.title)
      const body = encodeURIComponent(`${this.getShareText()}\n\n${this.getShareUrl()}`)
      window.open(`mailto:?subject=${subject}&body=${body}`, '_self')
      this.shareDialogOpen = false
    },
    shareViaMessages() {
      const text = encodeURIComponent(`${this.getShareText()} ${this.getShareUrl()}`)
      window.open(`sms:?&body=${text}`, '_self')
      this.shareDialogOpen = false
    },
    shareViaWhatsApp() {
      const text = encodeURIComponent(`${this.getShareText()} ${this.getShareUrl()}`)
      window.open(`https://wa.me/?text=${text}`, '_blank')
      this.shareDialogOpen = false
    },
    shareViaMessenger() {
      const url = encodeURIComponent(this.getShareUrl())
      window.open(`https://www.facebook.com/dialog/send?link=${url}&app_id=966242223397117&redirect_uri=${encodeURIComponent(this.getShareUrl())}`, '_blank')
      this.shareDialogOpen = false
    },
    shareViaFacebook() {
      const url = encodeURIComponent(this.getShareUrl())
      window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}`, '_blank')
      this.shareDialogOpen = false
    },
    async resolveProperty() {
      const slug = this.$route.params.slug
      if (slug) {
        try {
          const response = await api.getPropertyBySlug(slug)
          this.propertyId = response.data.id
        } catch (error) {
          console.error('Failed to resolve property slug:', error)
        }
      }
    },
    fetchImageSortOrder() {
      api.getImageSortOrder('hillwood', this.propertyId)
        .then(response => {
          this.imageSortOrder = response.data
        })
        .catch(error => {
          console.error('Failed to load image sort order:', error)
          this.imageSortOrder = []
        })
    },
    saveImageSortOrder() {
      const filenames = this.sortedAssetItems.map(img => img.filename)
      api.saveImageSortOrder('hillwood', filenames, this.propertyId)
        .then(() => {
          console.log('Image sort order saved')
        })
        .catch(error => {
          console.error('Failed to save image sort order:', error)
        })
    },
    onDragStart(index) {
      this.dragIndex = index
    },
    onDragOver(event) {
      event.preventDefault()
    },
    onDrop(targetIndex) {
      if (this.dragIndex === null || this.dragIndex === targetIndex) return
      const items = [...this.sortedAssetItems]
      const [moved] = items.splice(this.dragIndex, 1)
      items.splice(targetIndex, 0, moved)
      this.imageSortOrder = items.map(img => img.filename)
      this.dragIndex = null
    },
    onDragEnd() {
      this.dragIndex = null
    },
    toggleSortMode() {
      if (this.sortMode) {
        this.saveImageSortOrder()
      }
      this.sortMode = !this.sortMode
    },
    fetchFees() {
      api.getFees(this.propertyId)
        .then(response => {
          this.fees = response.data
        })
        .catch(error => {
          console.error('Failed to load fees:', error)
          this.fees = []
        })
    },
    fetchDiscounts() {
      api.getActiveDiscounts(this.propertyId)
        .then(response => {
          this.discounts = response.data
        })
        .catch(error => {
          console.error('Failed to load discounts:', error)
          this.discounts = []
        })
    },
    fetchTotalPrice() {
      this.totalPriceLoading = true
      api.getTotalPrice(this.checkIn, this.checkOut)
        .then(response => {
          this.totalPrice = response.data
        })
        .catch(error => {
          console.error('Failed to load total price:', error)
          this.totalPrice = null
        })
        .finally(() => {
          this.totalPriceLoading = false
        })
    },
    fetchBlockedDates() {
      this.blockedDatesLoading = true
      const today = new Date()
      const sixMonthsLater = new Date(today.getFullYear(), today.getMonth() + 18, today.getDate())
      const startDate = today.toISOString().slice(0, 10)
      const endDate = sixMonthsLater.toISOString().slice(0, 10)
      api.getBlockedDates(startDate, endDate)
        .then(response => {
          this.blockedDates = response.data
        })
        .catch(error => {
          console.error('Failed to load blocked dates from Hospitable:', error)
          this.blockedDates = []
        })
        .finally(() => {
          this.blockedDatesLoading = false
        })
    },
    openLightbox(index) {
      this.lightboxIndex = index
      this.lightboxOpen = true
    },
    nextImage() {
      if (this.lightboxIndex < this.images.length - 1) {
        this.lightboxIndex++
      }
    },
    prevImage() {
      if (this.lightboxIndex > 0) {
        this.lightboxIndex--
      }
    },
    checkAvailability() {
      if (this.hasBlockedInRange()) {
        alert('Your selected dates include blocked dates. Please choose different dates.')
        return
      }
      if (this.checkIn && this.checkOut && this.totalPrice) {
        this.openPayPalCheckout()
      } else {
        alert(`Check-in: ${this.checkIn || 'Not set'}\nCheckout: ${this.checkOut || 'Not set'}\nGuests: ${this.guestCount}`)
      }
    },
    async openPayPalCheckout() {
      this.paypalCheckoutOpen = true
      this.paymentSuccess = false
      this.paymentError = null
      this.paymentMessage = ''
      this.cardFieldsEligible = false

      try {
        // Get client ID from backend if not cached
        if (!this.paypalClientId) {
          this.paypalLoading = true
          const resp = await api.getPayPalClientId()
          this.paypalClientId = resp.data.clientId
        }

        // Load PayPal SDK if not already loaded
        if (!this.paypalSdkLoaded) {
          await this.loadPayPalSdk(this.paypalClientId)
          this.paypalSdkLoaded = true
        }

        this.paypalLoading = false

        // Wait for DOM to render the containers
        await this.$nextTick()
        this.initPayPalButtons()
      } catch (error) {
        console.error('Failed to initialize PayPal:', error)
        this.paypalLoading = false
        this.paymentError = 'Failed to load payment options. Please try again.'
      }
    },
    loadPayPalSdk(clientId) {
      return new Promise((resolve, reject) => {
        // Remove existing script if any
        const existing = document.getElementById('paypal-sdk-script')
        if (existing) {
          existing.remove()
        }
        const script = document.createElement('script')
        script.id = 'paypal-sdk-script'
        script.src = `https://www.paypal.com/sdk/js?components=buttons&client-id=${clientId}&intent=capture&currency=USD`
        script.onload = resolve
        script.onerror = () => reject(new Error('Failed to load PayPal SDK'))
        document.head.appendChild(script)
      })
    },
    initPayPalButtons() {
      const self = this
      const container = document.getElementById('paypal-button-container')
      if (!container) return
      container.innerHTML = ''

      // Calculate total amount in dollars
      const totalAmount = this.getPayPalAmount()

      // Render PayPal buttons
      window.paypal.Buttons({
        createOrder: async () => {
          try {
            const description = `Booking: ${self.formatDisplay(self.checkIn)} - ${self.formatDisplay(self.checkOut)} (${self.totalPrice.nights} nights)`
            const response = await api.createPayPalOrder(totalAmount, description)
            const orderData = response.data
            if (orderData.id) {
              return orderData.id
            } else {
              const errorDetail = orderData.details && orderData.details[0]
              const errorMessage = errorDetail
                ? `${errorDetail.issue} ${errorDetail.description} (${orderData.debug_id})`
                : JSON.stringify(orderData)
              throw new Error(errorMessage)
            }
          } catch (error) {
            console.error(error)
            self.paymentError = 'Could not initiate PayPal checkout. Please try again.'
            throw error
          }
        },
        onApprove: async (data, actions) => {
          if (!self.guestName || !self.guestName.trim()) {
            self.paymentError = 'Please enter your full name before completing payment.'
            return
          }
          try {
            const bookingDetails = self.getBookingDetails()
            const response = await api.capturePayPalOrder(data.orderID, bookingDetails)
            const orderData = response.data

            const transaction =
              (orderData.purchase_units && orderData.purchase_units[0] &&
                orderData.purchase_units[0].payments &&
                (orderData.purchase_units[0].payments.captures && orderData.purchase_units[0].payments.captures[0] ||
                 orderData.purchase_units[0].payments.authorizations && orderData.purchase_units[0].payments.authorizations[0]))
            const errorDetail = orderData.details && orderData.details[0]

            if (errorDetail && errorDetail.issue === 'INSTRUMENT_DECLINED' && !data.card && actions) {
              return actions.restart()
            } else if (errorDetail || !transaction || transaction.status === 'DECLINED') {
              let errorMessage
              if (transaction) {
                errorMessage = `Transaction ${transaction.status}: ${transaction.id}`
              } else if (errorDetail) {
                errorMessage = `${errorDetail.description} (${orderData.debug_id})`
              } else {
                errorMessage = JSON.stringify(orderData)
              }
              throw new Error(errorMessage)
            } else {
              self.paymentSuccess = true
              self.paymentMessage = `Transaction ${transaction.status}: ${transaction.id}`
              if (orderData.hospitableWarning) {
                self.paymentError = orderData.hospitableWarning
              }
              console.log('Capture result', orderData)
            }
          } catch (error) {
            console.error(error)
            self.paymentError = `Sorry, your transaction could not be processed. ${error.message || error}`
          }
        },
        onError: (err) => {
          console.error('PayPal button error:', err)
          self.paymentError = 'An error occurred with PayPal. Please try again.'
        }
      }).render('#paypal-button-container')

      // Render card fields if eligible
      try {
        const cardField = window.paypal.CardFields({
          createOrder: async () => {
            try {
              const description = `Booking: ${self.formatDisplay(self.checkIn)} - ${self.formatDisplay(self.checkOut)} (${self.totalPrice.nights} nights)`
              const response = await api.createPayPalOrder(totalAmount, description, self.cardEmail || null)
              const orderData = response.data
              if (orderData.id) {
                return orderData.id
              }
              throw new Error(JSON.stringify(orderData))
            } catch (error) {
              console.error(error)
              self.paymentError = 'Could not initiate card payment. Please try again.'
              throw error
            }
          },
          onApprove: async (data) => {
            if (!self.guestName || !self.guestName.trim()) {
              self.paymentError = 'Please enter your full name before completing payment.'
              self.cardPaymentProcessing = false
              return
            }
            try {
              const bookingDetails = self.getBookingDetails()
              const response = await api.capturePayPalOrder(data.orderID, bookingDetails)
              const orderData = response.data
              const transaction =
                (orderData.purchase_units && orderData.purchase_units[0] &&
                  orderData.purchase_units[0].payments &&
                  (orderData.purchase_units[0].payments.captures && orderData.purchase_units[0].payments.captures[0] ||
                   orderData.purchase_units[0].payments.authorizations && orderData.purchase_units[0].payments.authorizations[0]))
              const errorDetail = orderData.details && orderData.details[0]

              if (errorDetail || !transaction || transaction.status === 'DECLINED') {
                let errorMessage = transaction
                  ? `Transaction ${transaction.status}: ${transaction.id}`
                  : errorDetail ? `${errorDetail.description} (${orderData.debug_id})` : JSON.stringify(orderData)
                throw new Error(errorMessage)
              } else {
                self.paymentSuccess = true
                self.paymentMessage = `Transaction ${transaction.status}: ${transaction.id}`
                if (orderData.hospitableWarning) {
                  self.paymentError = orderData.hospitableWarning
                }
                self.cardPaymentProcessing = false
                console.log('Card capture result', orderData)
              }
            } catch (error) {
              console.error(error)
              if (!self.paymentSuccess) {
                self.paymentError = `Sorry, your card transaction could not be processed. ${error.message || error}`
              }
              self.cardPaymentProcessing = false
            }
          },
          onError: (err) => {
            console.error('Card field error:', err)
            if (!self.paymentSuccess) {
              self.paymentError = 'An error occurred with card payment. Please try again.'
            }
            self.cardPaymentProcessing = false
          }
        })

        if (cardField.isEligible()) {
          self.cardFieldsEligible = true
          self.cardFieldInstance = cardField
          self.$nextTick(() => {
            cardField.NameField().render('#card-name-field-container')
            cardField.NumberField().render('#card-number-field-container')
            cardField.ExpiryField().render('#card-expiry-field-container')
            cardField.CVVField().render('#card-cvv-field-container')
          })
        }
      } catch (e) {
        console.log('Card fields not available:', e)
      }
    },
    getBookingDetails() {
      const totalAmount = this.grandTotal ? this.grandTotal.amount : this.totalPrice.totalAmount
      const description = `Booking: ${this.formatDisplay(this.checkIn)} - ${this.formatDisplay(this.checkOut)} (${this.totalPrice.nights} nights, ${this.guestSummary})`
      const details = {
        checkIn: this.checkIn,
        checkOut: this.checkOut,
        adults: this.adults,
        children: this.children,
        infants: this.infants,
        pets: this.pets,
        nights: this.totalPrice.nights,
        totalAmount: totalAmount,
        accommodationAmount: this.totalPrice.totalAmount,
        currency: this.totalPrice.currency || 'USD',
        description: description
      }
      if (this.propertyId) {
        details.propertyId = this.propertyId
      }
      if (this.guestName) {
        details.guestName = this.guestName
      }
      if (this.cardEmail) {
        details.guestEmail = this.cardEmail
      }
      if (this.discountTotal > 0) {
        details.discountAmount = this.discountTotal
        if (this.applicableDiscounts.length > 0 && this.applicableDiscounts[0].type) {
          details.discountType = this.applicableDiscounts[0].type
        }
      }
      return details
    },
    getPayPalAmount() {
      // Grand total in dollars formatted as string
      if (this.grandTotal) {
        return (this.grandTotal.amount / 100).toFixed(2)
      }
      return (this.totalPrice.totalAmount / 100).toFixed(2)
    },
    submitCardPayment() {
      if (!this.cardFieldInstance) return
      this.cardPaymentProcessing = true
      this.paymentError = null
      this.cardFieldInstance.submit().catch((error) => {
        console.error('Card submit error:', error)
        if (!this.paymentSuccess) {
          this.paymentError = `Sorry, your card transaction could not be processed. ${error.message || error}`
        }
        this.cardPaymentProcessing = false
      })
    },
    closePayPalCheckout() {
      this.paypalCheckoutOpen = false
      this.paymentError = null
      this.cardPaymentProcessing = false
      if (this.paymentSuccess) {
        // Reset form after successful payment
        this.clearDates()
        this.paymentSuccess = false
        this.paymentMessage = ''
      }
    },
    openDatePicker(field) {
      this.selecting = field
      this.datePickerOpen = true
    },
    closeDatePicker() {
      this.datePickerOpen = false
      this.selecting = null
    },
    selectPickerDate(dateStr) {
      if (this.selecting === 'checkin' || !this.checkIn || (this.checkIn && this.checkOut)) {
        this.checkIn = dateStr
        this.checkOut = ''
        this.selecting = 'checkout'
        this.minimumBookingError = false
      } else {
        if (dateStr <= this.checkIn) {
          this.checkIn = dateStr
          this.selecting = 'checkout'
          this.minimumBookingError = false
        } else {
          const checkInDate = new Date(this.checkIn + 'T00:00:00')
          const checkOutDate = new Date(dateStr + 'T00:00:00')
          const nights = Math.round((checkOutDate - checkInDate) / (1000 * 60 * 60 * 24))
          if (nights < this.minimumBookingDays) {
            this.minimumBookingError = true
            return
          }
          if (this.blockedDates.some(d => d > this.checkIn && d < dateStr)) {
            return
          }
          this.checkOut = dateStr
          this.selecting = null
          this.minimumBookingError = false
        }
      }
    },
    prevPickerMonth() {
      this.pickerStart = new Date(this.pickerStart.getFullYear(), this.pickerStart.getMonth() - 1, 1)
    },
    nextPickerMonth() {
      this.pickerStart = new Date(this.pickerStart.getFullYear(), this.pickerStart.getMonth() + 1, 1)
    },
    formatDisplay(dateStr) {
      if (!dateStr) return ''
      const d = new Date(dateStr + 'T00:00:00')
      return `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}/${d.getFullYear()}`
    },
    selectCalendarDate(dateStr) {
      if (!this.checkIn || (this.checkIn && this.checkOut)) {
        // Start new selection
        this.checkIn = dateStr
        this.checkOut = ''
        this.minimumBookingError = false
      } else {
        // Set checkout
        if (dateStr <= this.checkIn) {
          this.checkIn = dateStr
          this.minimumBookingError = false
        } else {
          const checkInDate = new Date(this.checkIn + 'T00:00:00')
          const checkOutDate = new Date(dateStr + 'T00:00:00')
          const nights = Math.round((checkOutDate - checkInDate) / (1000 * 60 * 60 * 24))
          if (nights < this.minimumBookingDays) {
            this.minimumBookingError = true
            return
          }
          if (this.blockedDates.some(d => d > this.checkIn && d < dateStr)) {
            return
          }
          this.checkOut = dateStr
          this.minimumBookingError = false
        }
      }
    },
    clearDates() {
      this.checkIn = ''
      this.checkOut = ''
      this.totalPrice = null
    },
    isCheckoutCandidate(dateStr) {
      if (!this.checkIn || this.checkOut || !this.blockedDates.includes(dateStr)) return false
      if (dateStr <= this.checkIn) return false
      const d = new Date(dateStr + 'T00:00:00')
      d.setDate(d.getDate() - 1)
      const prevDateStr = formatDate(d)
      return !this.blockedDates.includes(prevDateStr)
    },
    prevMonth() {
      this.calendarStart = new Date(this.calendarStart.getFullYear(), this.calendarStart.getMonth() - 1, 1)
    },
    nextMonth() {
      this.calendarStart = new Date(this.calendarStart.getFullYear(), this.calendarStart.getMonth() + 1, 1)
    },
    hasBlockedInRange() {
      if (!this.checkIn || !this.checkOut) return false
      return this.blockedDates.some(d => d > this.checkIn && d < this.checkOut)
    },
    fetchMinimumBookingDays() {
      api.getMinimumBookingDays()
        .then(response => {
          this.minimumBookingDays = response.data.minimumBookingDays || 2
        })
        .catch(error => {
          console.error('Failed to load minimum booking days:', error)
          this.minimumBookingDays = 2
        })
    },
    fetchShowPriceBreakdowns() {
      api.getShowPriceBreakdowns()
        .then(response => {
          this.showPriceBreakdowns = response.data.showPriceBreakdowns
        })
        .catch(() => {
          this.showPriceBreakdowns = true
        })
    },
    recordPageVisit() {
      const page = this.$route.path || '/hillwood'
      api.recordVisit(page).catch(err => {
        console.error('Failed to record visit:', err)
      })
    }
  }
}
</script>

<style scoped>
/* Share dialog */
.share-preview-image {
  width: 64px;
  height: 48px;
  object-fit: cover;
  border-radius: 8px;
}

.share-options-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.share-option-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 16px;
  border: 1px solid #ddd;
  border-radius: 12px;
  background: white;
  cursor: pointer;
  transition: background-color 0.15s, box-shadow 0.15s;
  min-height: 72px;
}

.share-option-btn:hover {
  background-color: #f7f7f7;
  box-shadow: 0 2px 4px rgba(0,0,0,0.08);
}

.share-option-btn:active {
  background-color: #ebebeb;
}

.photo-grid-wrapper {
  max-width: 1310px;
  margin: 0 auto;
}

.content-wrapper {
  max-width: 1375px;
  margin: 0 auto;
  padding-left: 20px;
}

.toolbar-logo {
  height: 36px;
  width: auto;
  object-fit: contain;
}

.photo-grid {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr;
  grid-template-rows: 1fr 1fr;
  gap: 8px;
  border-radius: 12px;
  overflow: hidden;
  cursor: pointer;
  aspect-ratio: 20 / 9;
}

.photo-main {
  grid-column: 1 / 3;
  grid-row: 1 / 3;
}

.photo-top-left {
  grid-column: 3 / 4;
  grid-row: 1 / 2;
}

.photo-top-right {
  grid-column: 4 / 5;
  grid-row: 1 / 2;
}

.photo-bottom-left {
  grid-column: 3 / 4;
  grid-row: 2 / 3;
}

.photo-bottom-right {
  grid-column: 4 / 5;
  grid-row: 2 / 3;
  position: relative;
}

.grid-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  transition: brightness 0.2s;
}

.grid-image:hover {
  filter: brightness(0.85);
}

.show-all-btn {
  position: absolute;
  bottom: 16px;
  right: 16px;
  text-transform: none !important;
  font-weight: 600;
  background-color: white !important;
  color: #222 !important;
  border: 1px solid #222 !important;
  border-radius: 8px !important;
}

/* Lightbox */
.lightbox-close {
  position: absolute;
  top: 8px;
  left: 8px;
  z-index: 10;
}

.lightbox-image {
  max-width: 100%;
  max-height: 500px;
  object-fit: contain;
}

.lightbox-nav {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 10;
}

.lightbox-prev {
  left: 8px;
}

.lightbox-next {
  right: 8px;
}

.cursor-pointer {
  cursor: pointer;
}

/* Availability card */
.availability-card {
  position: sticky;
  top: 80px;
  overflow: visible;
}

.date-row {
  display: flex;
  border: 1px solid #b0b0b0;
  border-radius: 8px 8px 0 0;
  overflow: hidden;
}

.date-field {
  flex: 1;
  padding: 10px 12px 6px;
  cursor: pointer;
}

.date-field-left {
  border-right: 1px solid #b0b0b0;
}

.date-field-active {
  border: 2px solid #222;
  border-radius: 8px;
  margin: -1px;
}

.date-label {
  display: block;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #222;
}

.date-input {
  width: 100%;
  border: none;
  outline: none;
  font-size: 14px;
  color: #717171;
  padding: 2px 0;
  background: transparent;
  cursor: pointer;
}

/* Datepicker popup */
.datepicker-popup {
  position: absolute;
  top: 100%;
  right: 0;
  z-index: 100;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 6px 20px rgba(0,0,0,0.2);
  padding: 24px;
  min-width: 660px;
  margin-top: 4px;
}

.datepicker-header {
  margin-bottom: 16px;
}

.datepicker-months {
  display: flex;
  gap: 32px;
}

.datepicker-month {
  flex: 1;
  min-width: 270px;
}

.datepicker-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px solid #ebebeb;
}

.guest-field {
  border: 1px solid #b0b0b0;
  border-top: none;
  border-radius: 0 0 8px 8px;
  padding: 10px 12px 4px;
}

.guest-select {
  margin-top: -4px;
}

/* Guest picker popup */
.guest-picker-popup {
  position: absolute;
  top: calc(100% - 60px);
  left: 24px;
  right: 24px;
  z-index: 100;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 6px 20px rgba(0,0,0,0.2);
  padding: 20px 24px 16px;
  margin-top: 4px;
}

.guest-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 0;
  border-bottom: 1px solid #ebebeb;
}

.guest-row-title {
  font-size: 16px;
  font-weight: 600;
  color: #222;
}

.guest-row-subtitle {
  font-size: 14px;
  color: #717171;
}

.description-truncated {
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* Calendar */
.calendar-wrapper {
  display: flex;
  gap: 32px;
  flex-wrap: wrap;
}

.calendar-month {
  flex: 1;
  min-width: 260px;
}

.calendar-month-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.calendar-day-names {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  text-align: center;
  margin-bottom: 4px;
}

.calendar-day-name {
  font-size: 12px;
  font-weight: 600;
  color: #717171;
  padding: 4px 0;
}

.calendar-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  text-align: center;
}

.calendar-cell {
  position: relative;
  padding: 8px 0;
  font-size: 14px;
  border-radius: 50%;
  width: 38px;
  height: 38px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 1px auto;
}

.calendar-selectable {
  cursor: pointer;
  font-weight: 500;
}

.calendar-selectable:hover {
  background-color: #f0f0f0;
  border-radius: 50%;
}

.calendar-blocked {
  color: #b0b0b0;
  cursor: not-allowed;
}

.calendar-strikethrough {
  text-decoration: line-through;
}

.calendar-past {
  color: #ccc;
  cursor: default;
}

.calendar-today {
  border: 1px solid #222;
  border-radius: 50%;
}

.calendar-selected {
  background-color: #222 !important;
  color: #fff !important;
  border-radius: 50%;
  font-weight: 700;
}

.calendar-in-range {
  background-color: #f0f0f0;
  border-radius: 0;
}

.legend-box {
  width: 16px;
  height: 16px;
  border-radius: 4px;
  display: inline-block;
}

.legend-blocked {
  background-color: #e0e0e0;
  position: relative;
}

.legend-blocked::after {
  content: '';
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  height: 1px;
  background: #999;
}

.legend-available {
  background-color: #fff;
  border: 1px solid #222;
}

.legend-selected {
  background-color: #222;
}

/* Total price yellow highlight */
.total-price-highlight {
  background: linear-gradient(180deg, transparent 55%, #ffe066 55%);
  padding: 0 2px;
}

/* Price breakdown */
.price-breakdown {
  padding-top: 4px;
}

/* Sort mode drag-and-drop */
.sort-item {
  position: relative;
  cursor: grab;
  border: 2px solid transparent;
  border-radius: 12px;
  transition: border-color 0.2s, opacity 0.2s, box-shadow 0.2s;
}

.sort-item:hover {
  border-color: #222;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.sort-item-dragging {
  opacity: 0.4;
  border-color: #E31C5F;
}

.sort-handle {
  position: absolute;
  top: 12px;
  left: 12px;
  z-index: 5;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 8px;
  padding: 4px 10px;
  backdrop-filter: blur(4px);
}

/* PayPal card form */
.card-form-container {
  padding-top: 4px;
}

#card-name-field-container,
#card-number-field-container,
#card-expiry-field-container,
#card-cvv-field-container {
  min-height: 40px;
  border: 1px solid #b0b0b0;
  border-radius: 8px;
  padding: 4px 8px;
}

/* Map container */
.map-container {
  position: relative;
  background: linear-gradient(135deg, #f5f5f5 0%, #e8e8e8 100%);
}

/* Responsive: stack on small screens */
@media (max-width: 768px) {
  .photo-grid {
    grid-template-columns: 1fr;
    grid-template-rows: auto;
    max-height: none;
  }

  .photo-main {
    grid-column: 1;
    grid-row: auto;
  }

  .photo-top-left,
  .photo-top-right,
  .photo-bottom-left,
  .photo-bottom-right {
    grid-column: 1;
    grid-row: auto;
  }

  .photo-grid > div {
    height: 250px;
  }

  .photo-main {
    height: 350px;
  }
}
</style>
